import React, { useMemo, useCallback, forwardRef, createContext, useContext, Children } from 'react';
import {
    Autocomplete,
    TextField,
    FormControl,
    FormHelperText,
    useMediaQuery,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import { trakrefBlue, red, Grey3, Grey4 } from '../../../color_utils';
import { trakrefFontFamily, trakrefFontFamilySemiBold } from '../../../utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';

const VIRTUALIZE_THRESHOLD = 200;
const LISTBOX_PADDING = 8;
const VIRTUAL_ROW_HEIGHT = 48;
const VIRTUAL_VISIBLE_ROWS = 8;

const renderVirtualRow = ({ data, index, style }) => (
    React.cloneElement(data[index], {
        style: { ...style, top: style.top + LISTBOX_PADDING },
    })
);

// MUI passes ListboxProps to the outer <ul>; redirect them to react-window's outer <div> via context
const OuterElementContext = createContext({});
const OuterElementType = forwardRef((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

const VirtualListbox = forwardRef(function VirtualListbox({ children, ...other }, ref) {
    const items = Children.toArray(children);
    const height = Math.min(items.length, VIRTUAL_VISIBLE_ROWS) * VIRTUAL_ROW_HEIGHT + 2 * LISTBOX_PADDING;
    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <FixedSizeList
                    itemData={items}
                    height={height}
                    width="100%"
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={VIRTUAL_ROW_HEIGHT}
                    overscanCount={5}
                    itemCount={items.length}
                >
                    {renderVirtualRow}
                </FixedSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const SearchableDropdown = ({
    options,
    getOptionLabel,
    renderOption,
    searchFields,
    groupBy,
    groupOrder,
    maxVisibleRows,
    rowHeight,
    emptyStateText,
    placeholder,
    label,
    value,
    onChange,
    required,
    error,
    disabled,
    id,
    sx,
    width,
    customHelperText,
    getOptionDisabled,
    variant,
}) => {
    const isMobileView = useMediaQuery(theme => theme.breakpoints.down('sm'));

    // Bridge: consumer passes an id, MUI Autocomplete needs the full object
    const selectedOption = useMemo(() => {
        if (value === '' || value === null || value === undefined) return null;
        return options.find(opt => String(opt.id) === String(value)) ?? null;
    }, [options, value]);

    // Pre-sort options by groupOrder so MUI renders group headers in the right sequence
    const sortedOptions = useMemo(() => {
        if (!groupBy || !groupOrder || groupOrder.length === 0) return options;
        const orderMap = Object.fromEntries(groupOrder.map((g, i) => [g.toLowerCase(), i]));
        return [...options].sort((a, b) => {
            const groupA = String(groupBy(a) ?? '').toLowerCase();
            const groupB = String(groupBy(b) ?? '').toLowerCase();
            const orderA = orderMap[groupA] ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap[groupB] ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
    }, [options, groupBy, groupOrder]);

    // Filter by searchFields — no reranking, just include/exclude
    const handleFilterOptions = useCallback((opts, { inputValue }) => {
        if (!inputValue) return opts;
        const lower = inputValue.toLowerCase().trim();
        if (!lower) return opts;
        return opts.filter(option =>
            searchFields.some(field => {
                const val = option[field];
                return val && String(val).toLowerCase().includes(lower);
            })
        );
    }, [searchFields]);

    // Responsive max height
    const visibleRows = isMobileView ? maxVisibleRows.mobile : maxVisibleRows.desktop;
    const maxHeight = rowHeight * visibleRows;

    // Determine popup icon
    const PopupIcon = error ? ErrorOutlineIcon : KeyboardArrowDownIcon;
    const popupIconColor = error ? red : disabled ? 'rgba(0, 0, 0, 0.26)' : trakrefBlue;

    // FixedSizeList can't render Autocomplete's group headers, so fall back to scroll when grouped
    const shouldVirtualize = !groupBy && options.length > VIRTUALIZE_THRESHOLD;

    return (
        <FormControl
            sx={{ width: `${width}%` }}
            required={required}
            error={error}
            disabled={disabled}
        >
            <Autocomplete
                id={id}
                options={sortedOptions}
                value={selectedOption}
                onChange={(_, newValue) => onChange(newValue?.id ?? '')}
                getOptionLabel={(option) => (option ? getOptionLabel(option) : '')}
                renderOption={renderOption}
                groupBy={groupBy || undefined}
                filterOptions={handleFilterOptions}
                isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
                getOptionDisabled={getOptionDisabled}
                noOptionsText={emptyStateText}
                openOnFocus
                blurOnSelect
                disabled={disabled}
                popupIcon={<PopupIcon sx={{ color: popupIconColor, fontSize: '16px' }} />}
                ListboxComponent={shouldVirtualize ? VirtualListbox : undefined}
                ListboxProps={shouldVirtualize ? undefined : {
                    sx: {
                        maxHeight: `${maxHeight}px`,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(0,0,0,0.18)',
                            borderRadius: '3px',
                        },
                    },
                }}
                slotProps={{
                    paper: {
                        sx: {
                            fontFamily: trakrefFontFamily,
                            '& .MuiAutocomplete-groupLabel': {
                                fontFamily: trakrefFontFamilySemiBold,
                                fontSize: '10px',
                                fontWeight: 700,
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                                color: Grey3,
                                backgroundColor: Grey4,
                                borderBottom: '1px solid #e0e0e0',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            },
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        required={required}
                        error={error}
                        placeholder={placeholder}
                        variant={variant}
                        InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,
                            sx: {
                                fontFamily: trakrefFontFamilySemiBold,
                                textTransform: 'uppercase',
                                color: disabled ? 'rgba(0, 0, 0, 0.38)' : trakrefBlue,
                                '&.Mui-focused': { color: trakrefBlue },
                                '&.Mui-error': { color: '#D51A1A !important' },
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: trakrefFontFamily,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: trakrefBlue,
                                },
                            },
                            '& .MuiInputBase-input': {
                                textAlign: 'left',
                                fontFamily: trakrefFontFamily,
                            },
                            ...sx,
                        }}
                    />
                )}
            />
            {error && !customHelperText && (
                <FormHelperText>This field is required</FormHelperText>
            )}
            {error && customHelperText && (
                <FormHelperText>{customHelperText}</FormHelperText>
            )}
        </FormControl>
    );
};

SearchableDropdown.propTypes = {
    options: PropTypes.array.isRequired,
    getOptionLabel: PropTypes.func,
    searchFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    renderOption: PropTypes.func,
    groupBy: PropTypes.func,
    groupOrder: PropTypes.arrayOf(PropTypes.string),
    maxVisibleRows: PropTypes.shape({
        desktop: PropTypes.number,
        mobile: PropTypes.number,
    }),
    rowHeight: PropTypes.number,
    emptyStateText: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    required: PropTypes.bool,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    sx: PropTypes.object,
    width: PropTypes.number,
    customHelperText: PropTypes.string,
    getOptionDisabled: PropTypes.func,
    variant: PropTypes.oneOf(['outlined', 'standard', 'filled']),
};

SearchableDropdown.defaultProps = {
    getOptionLabel: (option) => option.label ?? option.name ?? '',
    renderOption: undefined,
    groupBy: undefined,
    groupOrder: undefined,
    maxVisibleRows: { desktop: 10, mobile: 5.5 },
    rowHeight: 48,
    emptyStateText: 'No results found',
    placeholder: 'Type to search',
    value: '',
    required: false,
    error: false,
    disabled: false,
    id: '',
    sx: {},
    width: 100,
    customHelperText: undefined,
    getOptionDisabled: undefined,
    variant: 'outlined',
};

export default SearchableDropdown;
